import { DefaultGameState, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link MapComponent} */

const styles: string = css`
    :host {
        position: absolute;
        right: 5%;
        top: 5%;
        width: 20%;
        height: 60%;
    }

    #panic-meter-container {
        width: 100%;
        height: 100%;
    }

    #panic-meter-background {
        width: 100%;
        height: 30%;
        background: red;
    }

    #panic-meter-bar {
        height: 100%;
        background: #ffd433;
    }
`;

export class PanicMeterComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private timeoutAllowed: boolean = true;

    /** Current game state */
    private _currentGameState?: DefaultGameState;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
    }

    public disconnectedCallback(): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const err: Error = new Error();
        const stack: string[] = err.stack!.split("\n");

        if (stack[2].indexOf("HTMLElement") !== -1) {
            return;
        }
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }
        // Reset the component
        this._currentGameState = state;

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderPanicMeter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        if (this.timeoutAllowed) {
            this.timeoutAllowed = false;
            void this.removeHealth();
            this.timeout();
        }
    }

    private isUpdating: boolean = false; // Flag to prevent multiple updates

    private async removeHealth(): Promise<void> {
        const oldPanicPoint: number = this._currentGameState?.panicPoints ?? 100;
        if (oldPanicPoint < 100 && oldPanicPoint > 1) {
            if (this.isUpdating) return; // Prevent concurrent updates
            this.isUpdating = true;

            const newPanicPoint: number = oldPanicPoint - 0.40; // about 7.5 min but made a little longer because state changes also update it immediately

            try {
                const newState: GameState | undefined = await this._gameRouteService.updatePanicPoint(newPanicPoint);
                if (newState) {
                    this.updateGameState(newState);
                }
            }
            catch (error) {
                console.error("Failed to update panic points:", error);
            }
            finally {
                this.isUpdating = false;
            }
        }
    }

    private timeoutId: NodeJS.Timeout | null = null; // Store timeout ID

    private timeout(): void {
        if (this.timeoutId !== null) {
            return; // If a timeout is already running, do nothing
        }

        this.timeoutId = setTimeout(async () => {
            await this.removeHealth();
            this.timeoutId = null; // Reset timeout ID after execution
            this.timeout(); // Schedule the next timeout
        }, 2000);
    }

    private async changeRoom(): Promise<void> {
        const newState: GameState | undefined = await this._gameRouteService.changeRoom("game-over-screen");
        if (!newState) {
            return;
        }
        this.updateGameState(newState);
        this.updateGameState({ type: "switch-page", page: Page.Canvas } as GameState);
    }

    private renderPanicMeter(): HTMLElement {
        const panicPoints: number = this._currentGameState!.panicPoints;
        if (panicPoints < 1) {
            void this.changeRoom();
        }
        return html`
            <div id="panic-meter-container">
                <p>Panic meter</p>
                <div id="panic-meter-background">
                    <div style="width: ${panicPoints}%" id="panic-meter-bar"></div>
                </div>
            </div>
        `;
    }
}
